import { useMemo, useState } from 'react';
import { Helmet } from 'react-helmet-async';
import { DarkChapter } from '@/components/layout/DarkChapter';
import { ResultView } from '@/components/fanbase-fix/ResultView';
import { categoryLabels, playbookOrder, playbooks } from '@/features/fanbase-fix/playbooks';
import { fixtures } from '@/features/fanbase-fix/fixtures';
import { selectPlaybooks } from '@/features/fanbase-fix/selector';
import { QUESTIONNAIRE_VERSION, RULES_VERSION, resolveAnswers } from '@/features/fanbase-fix/types';
import { REVIEW_ENABLED } from '@/features/fanbase-fix/config';

type Tab = 'fixtures' | 'library';

export default function FanbaseFixReview() {
  const [tab, setTab] = useState<Tab>('fixtures');
  const [activeFixture, setActiveFixture] = useState(fixtures[0].id);

  const fixture = fixtures.find((f) => f.id === activeFixture)!;
  const selection = useMemo(() => selectPlaybooks(fixture.answers), [fixture]);
  const resolved = useMemo(() => resolveAnswers(fixture.answers), [fixture]);
  const result = useMemo(() => playbooks[selection.primary].build(resolved), [selection, resolved]);

  if (!REVIEW_ENABLED) return null;

  return (
    <>
      <Helmet>
        <title>Fanbase Fix internal review</title>
        <meta name="robots" content="noindex,nofollow" />
      </Helmet>

      <DarkChapter>
        <main className="min-h-screen px-6 py-12">
          <div className="mx-auto max-w-5xl">
            <div className="rounded-lg border border-amber-400/30 bg-amber-400/10 p-4">
              <p className="font-mono text-[11px] uppercase tracking-[0.15em] text-amber-200">
                Internal review only. Never shown to an artist.
              </p>
              <p className="mt-1 text-xs text-white/50">
                Questionnaire {QUESTIONNAIRE_VERSION} &middot; rules {RULES_VERSION} &middot; Stage 1: no data
                saved, no email sent, no AI.
              </p>
            </div>

            <div className="mt-6 flex gap-2">
              {(['fixtures', 'library'] as Tab[]).map((t) => (
                <button
                  key={t}
                  onClick={() => setTab(t)}
                  className={`rounded-lg px-4 py-2 text-sm font-medium transition-colors ${
                    tab === t ? 'bg-white text-[#1d1d1f]' : 'border border-white/15 text-white/70 hover:bg-white/10'
                  }`}
                >
                  {t === 'fixtures' ? 'Routing fixtures' : 'Playbook library'}
                </button>
              ))}
            </div>

            {tab === 'fixtures' && (
              <div className="mt-8">
                <div className="flex flex-wrap gap-2">
                  {fixtures.map((f) => (
                    <button
                      key={f.id}
                      onClick={() => setActiveFixture(f.id)}
                      className={`rounded-lg border px-3 py-2 text-left text-xs ${
                        f.id === activeFixture
                          ? 'border-[#0071e3] bg-[#0071e3]/15 text-white'
                          : 'border-white/12 text-white/60 hover:bg-white/5'
                      }`}
                    >
                      <span className="font-mono">{f.expectedPrimary}</span> &middot; {f.label}
                    </button>
                  ))}
                </div>

                <section className="mt-6 rounded-xl border border-white/12 bg-white/[0.03] p-5">
                  <h2 className="font-headline text-lg font-semibold text-white">The artist (fictional)</h2>
                  <p className="mt-2 text-[15px] leading-relaxed text-white/70">{fixture.story}</p>

                  <h3 className="mt-5 font-mono text-[11px] uppercase tracking-[0.15em] text-white/40">Answers</h3>
                  <pre className="mt-2 overflow-x-auto rounded-lg bg-black/50 p-4 font-mono text-[11px] leading-relaxed text-white/60">
{`goal: ${fixture.answers.goal.join(', ') || '(none)'}
readiness: ${fixture.answers.readiness.join(', ') || '(none)'}
song: ${fixture.answers.songTitle || '(none supplied)'}
Q2 audience: ${fixture.answers.audience.join(', ') || '(none)'}
Q3 discovery: ${fixture.answers.discovery.join(', ') || '(none)'}
Q4 introduction: ${fixture.answers.introduction.join(', ')}
Q5 response: ${fixture.answers.response.join(', ')}
Q6 next step: ${fixture.answers.nextStep.join(', ') || '(none)'}
Q7 return: ${fixture.answers.return.join(', ') || '(none)'}
Q8 learning: ${fixture.answers.learning.join(', ') || '(none)'}
Q9 capacity: ${fixture.answers.capacity.join(', ') || '(none)'}
personalisation:
${Object.entries(fixture.answers.personalisation)
  .map(([k, v]) => `  ${k}: ${v || '(blank)'}`)
  .join('\n')}`}
                  </pre>
                </section>

                <section className="mt-6 rounded-xl border border-white/12 bg-black/40 p-5">
                  <h2 className="font-mono text-[11px] uppercase tracking-[0.15em] text-white/40">
                    Decision trace (internal)
                  </h2>
                  <pre className="mt-3 overflow-x-auto font-mono text-[11px] leading-relaxed text-white/70">
{`RULES: ${selection.trace.rulesVersion}
GATE REACHED: ${selection.trace.gateReached}
PRIMARY: ${selection.primary}${selection.primary === fixture.expectedPrimary ? '' : `  [EXPECTED ${fixture.expectedPrimary}]`}
NEXT TO CHECK: ${selection.secondaries.join(', ') || 'None selected'}
RESOLVED FOR COPY: readiness=${resolved.readiness} capacity=${resolved.capacity} audience=${resolved.audience} nextStep=${resolved.nextStep} return=${resolved.return} learning=${resolved.learning}
TIE RULE: ${selection.trace.tieRuleApplied ?? 'none'}

GATE NOTES:
${selection.trace.gateNotes.map((n) => `- ${n}`).join('\n')}

CANDIDATES AND BLOCKS:
${selection.trace.entries
  .map((e) => `${e.status.padEnd(22)} ${e.playbookId.padEnd(13)} ${e.reasons.join(' | ')}`)
  .join('\n')}

OVERVIEW STATES:
${Object.entries(selection.overview)
  .map(([k, v]) => `${k.padEnd(11)} ${v.state}`)
  .join('\n')}`}
                  </pre>
                </section>

                <section className="mt-6 rounded-xl border border-white/12 p-1">
                  <p className="px-5 pt-4 font-mono text-[11px] uppercase tracking-[0.15em] text-white/40">
                    What the artist sees
                  </p>
                  <div className="pt-4">
                    <ResultView answers={fixture.answers} selection={selection} result={result} />
                  </div>
                </section>
              </div>
            )}

            {tab === 'library' && (
              <div className="mt-8 space-y-6">
                {playbookOrder.map((id) => {
                  const pb = playbooks[id];
                  const sample = pb.build(resolved);
                  return (
                    <article key={id} className="rounded-xl border border-white/12 bg-white/[0.03] p-5">
                      <div className="flex flex-wrap items-baseline gap-3">
                        <span className="rounded bg-[#0071e3]/20 px-2 py-1 font-mono text-xs text-[#7cbcff]">{pb.id}</span>
                        <h2 className="font-headline text-lg font-semibold text-white">{pb.name}</h2>
                        <span className="text-xs uppercase tracking-wide text-white/40">
                          {categoryLabels[pb.category]}
                        </span>
                      </div>

                      <p className="mt-3 text-sm font-semibold text-white/80">{pb.coreHeadline}</p>

                      <Grid>
                        <Block title="Problem it addresses">{pb.problemAddressed}</Block>
                        <Block title="Answer patterns that can trigger it">
                          <List items={pb.triggerPatterns} />
                        </Block>
                        <Block title="Gates required before it can be primary">
                          <List items={pb.gatesRequired} />
                        </Block>
                        <Block title="Asset it produces">{pb.assetProduced}</Block>
                        <Block title="Must not claim or infer">
                          <List items={pb.mustNotClaim} />
                        </Block>
                        <Block title="Cases that block selection">
                          <List items={pb.blockingCases} />
                        </Block>
                        <Block title="Personalisation fields requested">
                          <List items={pb.personalisation.map((f) => `${f.id}: ${f.label}`)} />
                        </Block>
                        <Block title="Approved base copy (headline and start-here)">
                          {`${sample.headline} | ${sample.startHereBody}`}
                        </Block>
                      </Grid>
                    </article>
                  );
                })}
                <p className="pb-16 text-xs text-white/35">
                  Base copy above is rendered against the currently selected fixture&apos;s answers, so you can see
                  how each playbook reads with real input. Switch fixture on the other tab to re-render.
                </p>
              </div>
            )}
          </div>
        </main>
      </DarkChapter>
    </>
  );
}

const Grid = ({ children }: { children: React.ReactNode }) => (
  <div className="mt-4 grid gap-4 md:grid-cols-2">{children}</div>
);

const Block = ({ title, children }: { title: string; children: React.ReactNode }) => (
  <div className="rounded-lg border border-white/8 bg-black/30 p-4">
    <p className="font-mono text-[10px] uppercase tracking-[0.15em] text-white/35">{title}</p>
    <div className="mt-2 text-sm leading-relaxed text-white/70">{children}</div>
  </div>
);

const List = ({ items }: { items: string[] }) => (
  <ul className="list-disc space-y-1 pl-4">
    {items.map((i, k) => (
      <li key={k}>{i}</li>
    ))}
  </ul>
);
